import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  Car, LayoutDashboard, BarChart3, CreditCard, LogOut, Loader2,
  ArrowLeft, ArrowRight, Check, Upload, Calendar, Target, DollarSign,
  Monitor, Building, Globe, Image, Video, X
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";

const API = '/api';

const STEPS = [
  { id: 1, title: "Campaign Type", icon: Target },
  { id: 2, title: "Targeting", icon: Monitor },
  { id: 3, title: "Schedule", icon: Calendar },
  { id: 4, title: "Creative", icon: Image },
  { id: 5, title: "Review", icon: Check }
];

export default function CreateCampaign() {
  const navigate = useNavigate();
  const [advertiser, setAdvertiser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [sites, setSites] = useState([]);
  const [screens, setScreens] = useState([]);
  const [priceEstimate, setPriceEstimate] = useState(null);
  const [uploadedContent, setUploadedContent] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    campaign_type: "standard",
    target_type: "site",
    target_ids: [],
    start_date: "",
    end_date: "",
    pricing_model: "flat_monthly",
    budget: ""
  });

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (advertiser) {
      fetchSites();
      fetchScreens();
    }
  }, [advertiser]);

  useEffect(() => {
    if (formData.target_ids.length > 0 && formData.start_date && formData.end_date) {
      fetchPriceEstimate();
    }
  }, [formData.campaign_type, formData.target_type, formData.target_ids, formData.start_date, formData.end_date]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/advertiser-portal/me`, { withCredentials: true });
      setAdvertiser(response.data);
    } catch (error) {
      navigate("/advertiser/login");
    } finally {
      setLoading(false);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await axios.get(`${API}/advertiser-portal/sites`, { withCredentials: true });
      setSites(response.data);
    } catch {}
  };

  const fetchScreens = async () => {
    try {
      const response = await axios.get(`${API}/advertiser-portal/screens`, { withCredentials: true });
      setScreens(response.data);
    } catch {}
  };

  const fetchPriceEstimate = async () => {
    try {
      const response = await axios.get(`${API}/advertiser-portal/pricing/estimate`, {
        params: {
          campaign_type: formData.campaign_type,
          target_type: formData.target_type,
          target_ids: formData.target_ids.join(","),
          start_date: formData.start_date,
          end_date: formData.end_date
        },
        withCredentials: true
      });
      setPriceEstimate(response.data);
    } catch {}
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    
    setUploading(true);
    
    for (const file of files) {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      formDataUpload.append("name", file.name);
      formDataUpload.append("content_type", file.type.startsWith("video") ? "video" : "image");
      
      try {
        const response = await axios.post(`${API}/advertiser-portal/content/upload`, formDataUpload, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" }
        });
        setUploadedContent(prev => [...prev, response.data]);
        toast.success(`Uploaded ${file.name}`);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setUploading(false);
  };

  const removeContent = (contentId) => {
    setUploadedContent(prev => prev.filter(c => c.content_id !== contentId));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        advertiser_id: advertiser.advertiser_id,
        name: formData.name,
        description: formData.description,
        campaign_type: formData.campaign_type,
        target: {
          target_type: formData.target_type,
          site_ids: formData.target_type === "site" ? formData.target_ids : [],
          screen_ids: formData.target_type === "screen" ? formData.target_ids : [],
          screen_types: []
        },
        schedule: {
          start_date: formData.start_date,
          end_date: formData.end_date,
          time_slots: [],
          days_of_week: [0, 1, 2, 3, 4, 5, 6]
        },
        pricing: {
          model: formData.pricing_model,
          flat_rate: formData.pricing_model === "flat_monthly" ? parseFloat(formData.budget) || null : null,
          total_budget: parseFloat(formData.budget) || null
        },
        content_ids: uploadedContent.map(c => c.content_id)
      };
      
      const response = await axios.post(`${API}/advertiser-portal/campaigns`, payload, { withCredentials: true });
      toast.success("Campaign created!");
      navigate(`/advertiser/campaigns/${response.data.campaign_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create campaign");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/advertiser-portal/logout`, {}, { withCredentials: true });
    } catch {}
    navigate("/advertiser/login");
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.name && formData.campaign_type;
      case 2:
        return formData.target_type && (formData.target_type === "network" || formData.target_ids.length > 0);
      case 3:
        return formData.start_date && formData.end_date;
      case 4:
        return uploadedContent.length > 0;
      default:
        return true;
    }
  };

  const toggleTarget = (id) => {
    setFormData(prev => ({
      ...prev,
      target_ids: prev.target_ids.includes(id)
        ? prev.target_ids.filter(t => t !== id)
        : [...prev.target_ids, id]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950" data-testid="create-campaign">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate("/advertiser/campaigns")}
                className="text-slate-400"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />Back
              </Button>
              <span className="text-slate-400">|</span>
              <span className="text-white font-medium">Create Campaign</span>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-400">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="bg-slate-900/50 border-b border-slate-800 overflow-x-auto">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between min-w-[500px]">
            {STEPS.map((step, idx) => (
              <div key={step.id} className="flex items-center">
                <div 
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                    currentStep === step.id 
                      ? "bg-blue-600 text-white" 
                      : currentStep > step.id 
                        ? "bg-emerald-600/20 text-emerald-400" 
                        : "bg-slate-800 text-slate-400"
                  }`}
                >
                  <step.icon className="w-4 h-4" />
                  <span className="text-sm font-medium hidden sm:inline">{step.title}</span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`w-8 sm:w-16 h-0.5 mx-2 ${currentStep > step.id ? "bg-emerald-600" : "bg-slate-700"}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Step 1: Campaign Type */}
        {currentStep === 1 && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">What type of campaign?</CardTitle>
              <CardDescription>Choose how you want your ads to appear</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="text-slate-300">Campaign Name *</Label>
                <Input
                  placeholder="Summer Sale Promotion"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white mt-2"
                  data-testid="campaign-name"
                />
              </div>
              <div>
                <Label className="text-slate-300">Description</Label>
                <Textarea
                  placeholder="Brief description of your campaign..."
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white mt-2"
                  rows={3}
                />
              </div>
              <div>
                <Label className="text-slate-300 mb-3 block">Campaign Type *</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { value: "standard", label: "Standard Rotation", desc: "Your ad plays in regular rotation" },
                    { value: "takeover_full", label: "Full Takeover", desc: "100% screen time during your campaign" },
                    { value: "takeover_wrapper", label: "Branded Wrapper", desc: "Your brand wraps around content" },
                    { value: "takeover_timeslot", label: "Time-slot Takeover", desc: "Own specific hours each day" }
                  ].map(type => (
                    <div
                      key={type.value}
                      onClick={() => setFormData({...formData, campaign_type: type.value})}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                        formData.campaign_type === type.value
                          ? "border-blue-500 bg-blue-500/10"
                          : "border-slate-700 bg-slate-900 hover:border-slate-600"
                      }`}
                    >
                      <p className="font-medium text-white">{type.label}</p>
                      <p className="text-sm text-slate-400 mt-1">{type.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Targeting */}
        {currentStep === 2 && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Where should your ads appear?</CardTitle>
              <CardDescription>Select your target locations</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="text-slate-300 mb-3 block">Target Type</Label>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { value: "site", label: "By Site", icon: Building },
                    { value: "screen", label: "By Screen", icon: Monitor },
                    { value: "network", label: "Network-wide", icon: Globe }
                  ].map(type => (
                    <div
                      key={type.value}
                      onClick={() => setFormData({...formData, target_type: type.value, target_ids: []})}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-colors text-center ${
                        formData.target_type === type.value
                          ? "border-blue-500 bg-blue-500/10"
                          : "border-slate-700 bg-slate-900 hover:border-slate-600"
                      }`}
                    >
                      <type.icon className="w-6 h-6 mx-auto mb-2 text-slate-300" />
                      <p className="font-medium text-white text-sm">{type.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {formData.target_type === "site" && (
                <div>
                  <Label className="text-slate-300 mb-3 block">Select Sites</Label>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {sites.map(site => (
                      <div
                        key={site.site_id}
                        onClick={() => toggleTarget(site.site_id)}
                        className={`p-3 rounded-lg border cursor-pointer flex items-center gap-3 ${
                          formData.target_ids.includes(site.site_id)
                            ? "border-blue-500 bg-blue-500/10"
                            : "border-slate-700 bg-slate-900 hover:border-slate-600"
                        }`}
                      >
                        <Checkbox checked={formData.target_ids.includes(site.site_id)} />
                        <div>
                          <p className="font-medium text-white">{site.name}</p>
                          <p className="text-sm text-slate-400">{site.address}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {formData.target_type === "screen" && (
                <div>
                  <Label className="text-slate-300 mb-3 block">Select Screens</Label>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {screens.map(screen => (
                      <div
                        key={screen.screen_id}
                        onClick={() => toggleTarget(screen.screen_id)}
                        className={`p-3 rounded-lg border cursor-pointer flex items-center gap-3 ${
                          formData.target_ids.includes(screen.screen_id)
                            ? "border-blue-500 bg-blue-500/10"
                            : "border-slate-700 bg-slate-900 hover:border-slate-600"
                        }`}
                      >
                        <Checkbox checked={formData.target_ids.includes(screen.screen_id)} />
                        <div>
                          <p className="font-medium text-white">{screen.name}</p>
                          <p className="text-sm text-slate-400">{screen.screen_type} • {screen.resolution}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {formData.target_type === "network" && (
                <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                  <p className="text-emerald-400">Your campaign will appear across all available screens in the network.</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Schedule */}
        {currentStep === 3 && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">When should it run?</CardTitle>
              <CardDescription>Set your campaign schedule and budget</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Start Date *</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    className="bg-slate-900 border-slate-700 text-white mt-2"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <Label className="text-slate-300">End Date *</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    className="bg-slate-900 border-slate-700 text-white mt-2"
                    min={formData.start_date || new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-300">Budget (£)</Label>
                <Input
                  type="number"
                  placeholder="1000"
                  value={formData.budget}
                  onChange={(e) => setFormData({...formData, budget: e.target.value})}
                  className="bg-slate-900 border-slate-700 text-white mt-2"
                />
              </div>

              {priceEstimate && (
                <div className="p-4 bg-slate-900 rounded-lg border border-slate-700">
                  <h4 className="text-sm font-medium text-slate-300 mb-3">Estimated Cost</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between text-slate-400">
                      <span>{priceEstimate.screens_count} screens × {priceEstimate.days} days</span>
                      <span>£{priceEstimate.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-slate-400">
                      <span>VAT (20%)</span>
                      <span>£{priceEstimate.vat.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-white font-semibold pt-2 border-t border-slate-700">
                      <span>Total</span>
                      <span>£{priceEstimate.total.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 4: Creative */}
        {currentStep === 4 && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Upload Your Creative</CardTitle>
              <CardDescription>Add images or videos for your campaign</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div 
                className="border-2 border-dashed border-slate-600 rounded-lg p-8 text-center hover:border-blue-500 transition-colors cursor-pointer"
                onClick={() => document.getElementById('file-upload').click()}
              >
                <input
                  id="file-upload"
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  className="hidden"
                  onChange={handleFileUpload}
                />
                {uploading ? (
                  <Loader2 className="w-12 h-12 text-blue-500 mx-auto mb-4 animate-spin" />
                ) : (
                  <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                )}
                <p className="text-white font-medium">Click to upload or drag and drop</p>
                <p className="text-sm text-slate-400 mt-1">PNG, JPG, MP4 up to 50MB</p>
              </div>

              {uploadedContent.length > 0 && (
                <div>
                  <Label className="text-slate-300 mb-3 block">Uploaded Content</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {uploadedContent.map(content => (
                      <div key={content.content_id} className="relative group">
                        <div className="aspect-video bg-slate-900 rounded-lg overflow-hidden border border-slate-700">
                          {content.content_type === "video" ? (
                            <div className="w-full h-full flex items-center justify-center">
                              <Video className="w-8 h-8 text-slate-400" />
                            </div>
                          ) : (
                            <img 
                              src={content.file_url} 
                              alt={content.name}
                              className="w-full h-full object-cover"
                            />
                          )}
                        </div>
                        <button
                          onClick={() => removeContent(content.content_id)}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-4 h-4 text-white" />
                        </button>
                        <p className="text-xs text-slate-400 mt-1 truncate">{content.name}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 5: Review */}
        {currentStep === 5 && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Review Your Campaign</CardTitle>
              <CardDescription>Make sure everything looks correct before submitting</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-slate-900 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase">Campaign Name</p>
                  <p className="text-white font-medium mt-1">{formData.name}</p>
                </div>
                <div className="p-4 bg-slate-900 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase">Campaign Type</p>
                  <p className="text-white font-medium mt-1 capitalize">{formData.campaign_type.replace("_", " ")}</p>
                </div>
                <div className="p-4 bg-slate-900 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase">Target</p>
                  <p className="text-white font-medium mt-1">
                    {formData.target_type === "network" ? "Network-wide" : `${formData.target_ids.length} ${formData.target_type}s`}
                  </p>
                </div>
                <div className="p-4 bg-slate-900 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase">Schedule</p>
                  <p className="text-white font-medium mt-1">{formData.start_date} to {formData.end_date}</p>
                </div>
                <div className="p-4 bg-slate-900 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase">Content Items</p>
                  <p className="text-white font-medium mt-1">{uploadedContent.length} files</p>
                </div>
                <div className="p-4 bg-slate-900 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase">Budget</p>
                  <p className="text-white font-medium mt-1">£{formData.budget || "Not set"}</p>
                </div>
              </div>

              {priceEstimate && (
                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-blue-300 font-medium">Estimated Total</p>
                      <p className="text-sm text-blue-400">{priceEstimate.screens_count} screens × {priceEstimate.days} days</p>
                    </div>
                    <p className="text-2xl font-bold text-white">£{priceEstimate.total.toFixed(2)}</p>
                  </div>
                </div>
              )}

              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-amber-300 text-sm">
                  <strong>Note:</strong> Your campaign will be submitted for review. Our team will approve it within 24-48 hours.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            onClick={() => setCurrentStep(prev => prev - 1)}
            disabled={currentStep === 1}
            className="border-slate-700 text-slate-300"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />Back
          </Button>
          
          {currentStep < 5 ? (
            <Button
              onClick={() => setCurrentStep(prev => prev + 1)}
              disabled={!canProceed()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Continue<ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="submit-campaign"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Create Campaign
            </Button>
          )}
        </div>
      </main>
    </div>
  );
}
